/**
 * ModalFlow — a single-Dialog host for multi-step flows.
 *
 * The problem it solves:
 *   Rendering N separate <Modal> / <MultiStepsModal> components and toggling
 *   `isOpen` between them causes the backdrop to flash: the exiting modal's
 *   overlay plays its fade-out animation while the entering one plays fade-in,
 *   creating a visible flicker and re-triggering the focus trap.
 *
 * The fix:
 *   ModalFlow owns ONE <Dialog> + <DialogContent> for the whole flow.
 *   The backdrop and popup mount when the flow opens and stay mounted until the
 *   flow closes, regardless of which step is active.  Inside the content, only
 *   the <ModalFlowStep when={step}> that matches the current `step` is rendered.
 *
 * Usage:
 *   ```tsx
 *   const flow = useModalFlow<'confirm' | 'success'>();
 *
 *   <Button onClick={() => flow.open('confirm')}>Start</Button>
 *
 *   <ModalFlow isOpen={flow.isOpen} step={flow.step ?? ''} onOpenChange={open => { if (!open) flow.close(); }}>
 *     <ModalFlowStep when="confirm">
 *       <DialogHeader>…</DialogHeader>
 *       …
 *       <DialogFooter>…</DialogFooter>
 *     </ModalFlowStep>
 *
 *     <ModalFlowStep when="success">
 *       …
 *     </ModalFlowStep>
 *   </ModalFlow>
 *   ```
 */

import {createContext, useContext, useMemo} from 'react';

import {modalSizeVariants} from './modal.config';
import {useDiscardGuard} from './useDiscardGuard';
import {cn} from '../../lib/utils';
import {Dialog, DialogContent} from '../shadcn/dialog';

import type {ModalOutsidePress, ModalSize} from './modal.types';
import type React from 'react';
import type {ReactNode} from 'react';

// ─── Context ─────────────────────────────────────────────────────────────────

interface ModalFlowContextValue {
  /** Identifier of the currently active step. */
  step: string;
}

const ModalFlowContext = createContext<ModalFlowContextValue | null>(null);

/**
 * Returns the current ModalFlow context.
 * Must be called inside a <ModalFlow> tree; throws otherwise.
 */
export function useModalFlowContext(): ModalFlowContextValue {
  const ctx = useContext(ModalFlowContext);
  if (!ctx) throw new Error('useModalFlowContext must be called inside a <ModalFlow>.');
  return ctx;
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface ModalFlowProps {
  /** Whether the flow dialog is open. */
  isOpen: boolean;
  /**
   * Called when the dialog requests to be closed (Escape key, backdrop click).
   * Typically you set isOpen to false and reset flow state here.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Identifier of the currently active step.  Must match the `when` prop of
   * exactly one <ModalFlowStep> child for that step's content to be shown.
   */
  step: string;
  /** <ModalFlowStep> children (and any other static nodes). */
  children: ReactNode;
  /** Maximum width of the dialog panel. Defaults to 'md'. */
  size?: ModalSize;
  /** Additional class applied to the dialog content panel. */
  className?: string;
  /** Disable focus trap and scroll lock (e.g. inside Storybook). */
  disableFocusTrap?: boolean;
  /** Portal container — pass a ref or element to scope the portal. */
  portalContainer?: React.RefObject<HTMLElement | null> | HTMLElement | null;
  /** Show the default × close button. Defaults to true. */
  showCloseButton?: boolean;
  outsidePress?: ModalOutsidePress;
  isDirty?: boolean;
  discardDescription?: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * A single Dialog host for multi-step modal flows.
 * Keeps one backdrop + focus-trap alive for the entire flow.
 */
export function ModalFlow({
  isOpen,
  onOpenChange,
  step,
  children,
  size = 'md',
  className,
  disableFocusTrap = false,
  portalContainer,
  showCloseButton = true,
  outsidePress,
  isDirty = false,
  discardDescription
}: ModalFlowProps) {
  const ctx = useMemo<ModalFlowContextValue>(() => ({step}), [step]);
  const {handleOpenChange, discardModal} = useDiscardGuard({
    isOpen,
    isDirty,
    onOpenChange,
    discardDescription
  });

  return (
    <ModalFlowContext value={ctx}>
      <Dialog
        open={isOpen}
        onOpenChange={handleOpenChange}
        modal={!disableFocusTrap}
        outsidePress={outsidePress}
      >
        <DialogContent
          showCloseButton={showCloseButton}
          className={cn(modalSizeVariants({size}), className)}
          initialFocus={disableFocusTrap ? false : undefined}
          container={portalContainer}
        >
          {children}
          {discardModal}
        </DialogContent>
      </Dialog>
    </ModalFlowContext>
  );
}
